<template>
  <div class="personal-center-container">
    <!-- 固定在顶部的头部 -->
    <div class="page-header-fixed">
      <div class="header-actions">
        <el-button
            class="back-button"
            icon="el-icon-arrow-left"
            @click="goBack"
            size="medium"
        >
          返回
        </el-button>
      </div>
      <h1 class="page-title">个人中心</h1>
      <p class="page-subtitle">管理您的个人信息和账户设置</p>
    </div>

    <!-- 内容区域，添加顶部内边距以避免被固定头部遮挡 -->
    <div class="page-content">
      <el-row :gutter="20">
        <!-- 左侧个人信息卡片 -->
        <el-col :xs="24" :sm="8">
          <el-card class="profile-card" shadow="hover">
            <div slot="header" class="card-header">
              <i class="el-icon-user-solid header-icon"></i>
              <span class="header-text">个人信息</span>
            </div>

            <div class="profile-content">
              <!-- 头像区域 -->
              <div class="avatar-section">
                <div class="avatar-upload">
                  <el-avatar
                      :size="100"
                      :src="userInfo.avatar"
                      icon="el-icon-user-solid"
                      class="user-avatar"
                  ></el-avatar>
                  <el-upload
                      class="avatar-uploader"
                      :action="avatarUploadUrl"
                      :show-file-list="false"
                      :on-success="handleAvatarSuccess"
                      :before-upload="beforeAvatarUpload"
                      :headers="uploadHeaders"
                  >
                  </el-upload>
                </div>
              </div>

              <!-- 基本信息 -->
              <div class="basic-info">
                <div class="info-item">
                  <label class="info-label">用户名：</label>
                  <span class="info-value">{{ userInfo.username }}</span>
                </div>
                <div class="info-item">
                  <label class="info-label">姓名：</label>
                  <span class="info-value">{{ userInfo.realName || '未设置' }}</span>
                </div>
                <div class="info-item">
                  <label class="info-label">手机号：</label>
                  <span class="info-value">{{ userInfo.phone || '未设置' }}</span>
                </div>
                <div class="info-item">
                  <label class="info-label">角色：</label>
                  <el-tag :type="getRoleTagType(userInfo.role)" effect="plain" class="role-tag">
                    {{ getRoleText(userInfo.role) }}
                  </el-tag>
                </div>
                <div class="info-item">
                  <label class="info-label">注册时间：</label>
                  <span class="info-value">{{ formatDate(userInfo.createTime) }}</span>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧设置卡片 -->
        <el-col :xs="24" :sm="16">
          <el-card class="settings-card" shadow="hover">
            <div slot="header" class="card-header">
              <i class="el-icon-setting header-icon"></i>
              <span class="header-text">账户设置</span>
            </div>

            <el-tabs v-model="activeTab" class="settings-tabs">
              <!-- 基本信息编辑 -->
              <el-tab-pane label="基本信息" name="basic">
                <el-form :model="profileForm" :rules="profileRules" ref="profileForm" label-width="100px" class="profile-form">
                  <el-form-item label="用户名" prop="username">
                    <el-input v-model="profileForm.username" disabled class="disabled-input">
                      <template slot="append">
                        <el-tooltip content="用户名不可修改" placement="top">
                          <i class="el-icon-info"></i>
                        </el-tooltip>
                      </template>
                    </el-input>
                  </el-form-item>

                  <el-form-item label="真实姓名" prop="realName">
                    <el-input v-model="profileForm.realName" placeholder="请输入真实姓名" maxlength="10" show-word-limit></el-input>
                  </el-form-item>

                  <el-form-item label="手机号码" prop="phone">
                    <el-input v-model="profileForm.phone" placeholder="请输入手机号码" maxlength="11"></el-input>
                  </el-form-item>

                  <el-form-item label="邮箱地址">
                    <el-input
                        v-model="profileForm.email"
                        placeholder="请输入邮箱地址"
                        disabled
                    >
                      <template slot="append">
                        <el-tooltip content="邮箱功能暂未开放" placement="top">
                          <i class="el-icon-info"></i>
                        </el-tooltip>
                      </template>
                    </el-input>
                  </el-form-item>

                  <el-form-item label="个人简介">
                    <el-input
                        type="textarea"
                        v-model="profileForm.introduction"
                        placeholder="请输入个人简介"
                        :rows="4"
                        maxlength="200"
                        show-word-limit
                        disabled
                    >
                    </el-input>
                  </el-form-item>

                  <el-form-item>
                    <el-button type="primary" @click="updateProfile" :loading="loading.profile">
                      <i class="el-icon-check"></i>
                      保存修改
                    </el-button>
                    <el-button @click="resetProfileForm">重置</el-button>
                  </el-form-item>
                </el-form>
              </el-tab-pane>

              <!-- 修改密码 -->
              <el-tab-pane label="修改密码" name="password">
                <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px" class="password-form">
                  <el-form-item label="当前密码" prop="currentPassword">
                    <el-input
                        type="password"
                        v-model="passwordForm.currentPassword"
                        placeholder="请输入当前密码"
                        show-password
                    ></el-input>
                  </el-form-item>

                  <el-form-item label="新密码" prop="newPassword">
                    <el-input
                        type="password"
                        v-model="passwordForm.newPassword"
                        placeholder="请输入新密码"
                        show-password
                    ></el-input>
                    <div class="password-tips">
                      密码长度6-20位，包含字母和数字
                    </div>
                  </el-form-item>

                  <el-form-item label="确认密码" prop="confirmPassword">
                    <el-input
                        type="password"
                        v-model="passwordForm.confirmPassword"
                        placeholder="请再次输入新密码"
                        show-password
                    ></el-input>
                  </el-form-item>

                  <el-form-item>
                    <el-button type="primary" @click="updatePassword" :loading="loading.password">
                      <i class="el-icon-lock"></i>
                      修改密码
                    </el-button>
                    <el-button @click="resetPasswordForm">重置</el-button>
                  </el-form-item>
                </el-form>
              </el-tab-pane>
            </el-tabs>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { userAPI } from '@/services/api'

export default {
  name: 'PersonalCenter',
  data() {
    // 确认密码验证规则
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      activeTab: 'basic',
      loading: {
        profile: false,
        password: false,
        notification: false
      },
      userInfo: {
        username: '',
        realName: '',
        phone: '',
        email: '',
        role: '',
        avatar: '',
        createTime: ''
      },
      profileForm: {
        username: '',
        realName: '',
        phone: '',
        email: '',
        introduction: ''
      },
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      notificationSettings: {
        appointmentReminder: true,
        systemNotification: true,
        smsNotification: false,
        emailNotification: true
      },
      // 头像上传
      avatarUploadUrl: 'http://localhost:8081/api/user/upload-avatar',
      uploadHeaders: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      // 表单验证规则
      profileRules: {
        realName: [
          {required: true, message: '请输入真实姓名', trigger: 'blur'},
          {min: 2, max: 10, message: '姓名长度在 2 到 10 个字符', trigger: 'blur'}
        ],
        phone: [
          {required: true, message: '请输入手机号码', trigger: 'blur'},
          {pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur'}
        ]
      },
      passwordRules: {
        currentPassword: [
          {required: true, message: '请输入当前密码', trigger: 'blur'},
          {min: 6, message: '密码长度不能少于6位', trigger: 'blur'}
        ],
        newPassword: [
          {required: true, message: '请输入新密码', trigger: 'blur'},
          {min: 6, message: '密码长度不能少于6位', trigger: 'blur'},
          {pattern: /^(?=.*[A-Za-z])(?=.*\d).{6,20}$/, message: '密码必须包含字母和数字', trigger: 'blur'}
        ],
        confirmPassword: [
          {required: true, message: '请确认新密码', trigger: 'blur'},
          {validator: validateConfirmPassword, trigger: 'blur'}
        ]
      }
    }
  },
  mounted() {
    this.loadUserInfo()
    // 更新上传header的token
    this.uploadHeaders.Authorization = `Bearer ${localStorage.getItem('token')}`
  },
  methods: {
    // 添加返回上一页方法
    goBack() {
      // 使用Vue Router返回上一页
      this.$router.go(-1)
      // 或者也可以使用以下方式返回
      // this.$router.back()
    },

    // 加载用户信息
    async loadUserInfo() {
      try {
        // 先从localStorage加载基本信息
        const userInfoStr = localStorage.getItem('userInfo')
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr)
        }

        // 从API获取完整的个人信息
        const response = await userAPI.getProfile()
        if (response.success) {
          const profileData = response.data

          // 更新用户信息
          this.userInfo = {
            ...this.userInfo,
            ...profileData
          }

          // 初始化表单数据
          this.profileForm = {
            username: this.userInfo.username || '',
            realName: this.userInfo.realName || '',
            phone: this.userInfo.phone || '',
            email: this.userInfo.email || '', // 后端返回空字符串
            introduction: this.userInfo.introduction || '' // 后端返回空字符串
          }
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        // 如果API失败，fallback到localStorage
        this.fallbackToLocalStorage()
      }
    },

    // 更新个人信息
    async updateProfile() {
      this.$refs.profileForm.validate(async (valid) => {
        if (valid) {
          this.loading.profile = true
          try {
            // 只提交后端实际处理的字段
            const updateData = {
              realName: this.profileForm.realName,
              phone: this.profileForm.phone
              // email和introduction后端不处理，但可以提交空值
            }

            // 调用API更新个人信息
            const response = await userAPI.updateProfile(updateData)

            if (response.success) {
              // 更新本地存储的用户信息
              const updatedUserInfo = {
                ...this.userInfo,
                realName: this.profileForm.realName,
                phone: this.profileForm.phone
              }
              localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo))
              this.userInfo = updatedUserInfo

              this.$message.success('个人信息更新成功')
            }
          } catch (error) {
            console.error('更新个人信息失败:', error)
            this.$message.error('更新失败: ' + (error.message || '未知错误'))
          } finally {
            this.loading.profile = false
          }
        } else {
          this.$message.warning('请完善表单信息')
        }
      })
    },

    // 修改密码
    async updatePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (valid) {
          this.loading.password = true
          try {
            await userAPI.changePassword(this.passwordForm)
            this.$message.success('密码修改成功')
            this.resetPasswordForm()
          } catch (error) {
            console.error('修改密码失败:', error)
            this.$message.error('修改失败: ' + (error.message || '未知错误'))
          } finally {
            this.loading.password = false
          }
        } else {
          this.$message.warning('请完善表单信息')
        }
      })
    },

    // 保存通知设置
    async saveNotificationSettings() {
      this.loading.notification = true
      try {
        // 调用API保存通知设置
        await userAPI.saveNotificationSettings(this.notificationSettings)
        this.$message.success('通知设置保存成功')
      } catch (error) {
        console.error('保存通知设置失败:', error)
        this.$message.error('保存失败')
      } finally {
        this.loading.notification = false
      }
    },

    // 头像上传相关方法
    async handleAvatarSuccess(response) {
      try {
        if (response.success) {
          this.userInfo.avatar = response.data.avatarUrl
          this.$message.success('头像上传成功')
        } else {
          this.$message.error('头像上传失败: ' + response.message)
        }
      } catch (error) {
        console.error('头像上传失败:', error)
        this.$message.error('头像上传失败')
      }
    },

    beforeAvatarUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isJPG) {
        this.$message.error('头像只能是 JPG 或 PNG 格式!')
      }
      if (!isLt2M) {
        this.$message.error('头像大小不能超过 2MB!')
      }
      return isJPG && isLt2M
    },

    // 重置表单
    resetProfileForm() {
      this.profileForm = {
        username: this.userInfo.username || '',
        realName: this.userInfo.realName || '',
        phone: this.userInfo.phone || '',
        email: this.userInfo.email || '',
        introduction: this.userInfo.introduction || ''
      }
      this.$refs.profileForm.clearValidate()
    },

    resetPasswordForm() {
      this.passwordForm = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.$refs.passwordForm.clearValidate()
    },

    // fallback到localStorage
    fallbackToLocalStorage() {
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        try {
          this.userInfo = JSON.parse(userInfoStr)
          this.profileForm = {
            username: this.userInfo.username || '',
            realName: this.userInfo.realName || '',
            phone: this.userInfo.phone || '',
            email: '',
            introduction: ''
          }
        } catch (error) {
          console.error('解析用户信息失败:', error)
          this.$message.error('加载用户信息失败')
        }
      }
    },

    // 工具方法
    getRoleTagType(role) {
      const types = {
        'student': 'success',
        'coach': 'warning',
        'admin': 'danger'
      }
      return types[role] || 'info'
    },

    getRoleText(role) {
      const texts = {
        'student': '学员',
        'coach': '教练',
        'admin': '管理员'
      }
      return texts[role] || role
    },

    formatDate(date) {
      if (!date) return '未知'
      try {
        return new Date(date).toLocaleDateString()
      } catch (error) {
        return '无效日期'
      }
    }
  }
}
</script>

<style scoped>
.personal-center-container {
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #f8fafc;
  min-height: 100vh;
  box-sizing: border-box;
}

/* ===== 固定在顶部的页面标题区域 ===== */
.page-header-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  text-align: center;
  background: #135185;
  padding: 24px;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease;
  margin: 0 auto;
  max-width: 1200px;
}

.page-header-fixed:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.header-actions {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #bfc0c6;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 15px;
  color: #bfc0c6;
  margin: 0;
}

/* ===== 内容区域 ===== */
.page-content {
  padding: 160px 24px 24px 24px; /* 顶部内边距要足够大，避免被固定头部遮挡 */
  margin-top: 0;
  box-sizing: border-box;
}

/* ===== 返回按钮 ===== */
.back-button {
  color: #1e3696;
  background-color: #92aeca;
  border: none;
  font-weight: 500;
  padding: 10px 18px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.back-button:hover {
  background-color: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.6);
}

.back-button:active {
  transform: scale(0.98);
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
}

/* ===== 卡片通用样式 ===== */
.profile-card,
.settings-card {
  border-radius: 12px;
  border: none;
  margin-bottom: 24px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.profile-card:hover,
.settings-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  background-color: #fafafa;
}

.header-icon {
  font-size: 18px;
  color: #409EFF;
  margin-right: 8px;
}

.header-text {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

/* ===== 个人信息区域 ===== */
.profile-content {
  padding: 24px;
}

.avatar-section {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.user-avatar {
  border: 3px solid #e8eaec;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.user-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 0 10px rgba(64, 158, 255, 0.3);
}

.basic-info {
  padding: 0 8px;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f2f5;
}

.info-label {
  width: 90px;
  font-weight: 500;
  color: #606266;
  font-size: 14px;
}

.info-value {
  flex: 1;
  color: #303133;
  font-size: 14px;
  word-break: break-all;
}

.role-tag {
  margin-left: 0;
}

/* ===== 设置表单 ===== */
.settings-tabs {
  padding: 0 16px;
}

.profile-form,
.password-form {
  max-width: 520px;
  margin: 24px 0;
}

.disabled-input {
  opacity: 0.7;
  cursor: not-allowed;
}

.password-tips {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* ===== 通知设置样式（保留兼容性） ===== */
.notification-settings {
  padding: 12px 0;
}

.section-title {
  font-size: 16px;
  color: #303133;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.notification-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f0f2f5;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-info h4 {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #303133;
}

.notification-info p {
  margin: 0;
  font-size: 13px;
  color: #909399;
}

/* ===== 响应式设计 ===== */
@media (max-width: 768px) {
  .personal-center-container {
    padding: 0;
  }

  .page-header-fixed {
    padding: 16px;
    border-radius: 0;
  }

  .page-content {
    padding: 140px 16px 16px 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-label {
    width: auto;
    margin-bottom: 4px;
  }

  .profile-form,
  .password-form {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .personal-center-container {
    padding: 0;
  }

  .page-header-fixed {
    padding: 14px;
    border-radius: 0;
  }

  .page-content {
    padding: 130px 10px 10px 10px;
  }

  .card-header {
    padding: 12px 15px;
  }

  .profile-content {
    padding: 16px;
  }

  .back-button {
    padding: 8px;
    width: 60px;
    justify-content: center;
  }

  .back-button span {
    display: none;
  }

  .back-button i {
    margin: 0;
  }
}
</style>
